/**
 * SettingsHub — role-filtered settings landing page.
 *
 * Wrapped in OperatorShell. Shows a grid of setting cards filtered by the
 * current user's role:
 *   - executive   : Company Settings, Email Settings, Integrations, Override Policy,
 *                   User Permissions (admin)
 *   - manager     : Team Settings (Override Policy), Email Settings, Activity Policy
 *   - ae / bdr    : Profile Settings (Account Settings), Email Settings
 *
 * Route: /#/settings (root settings landing)
 */
import { useLocation } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";
import { getSession } from "@/lib/session";

interface SettingsCard {
  title: string;
  description: string;
  route: string;
  roles: Array<"executive" | "manager" | "ae" | "bdr" | "marketing" | "se">;
  tag?: string;
}

const SETTINGS_CARDS: SettingsCard[] = [
  {
    title: "Company Settings",
    description: "Tenant-wide configuration: company name, fiscal year, quota cadence, and integration defaults.",
    route: "/settings/company",
    roles: ["executive"],
    tag: "Executive",
  },
  {
    title: "Override Policy",
    description: "Control whether managers must approve push-defense answers or reps can self-check.",
    route: "/settings/override-policy",
    roles: ["executive", "manager"],
    tag: "Manager / Executive",
  },
  {
    title: "Email Settings",
    description: "Manage email notifications, digest frequency, and deal-alert thresholds for your account.",
    route: "/settings/email",
    roles: ["executive", "manager", "ae", "bdr", "marketing", "se"],
  },
  {
    title: "Integrations",
    description: "Connect CRM, calendar, and data sources. OAuth flows managed here.",
    route: "/settings/integrations",
    roles: ["executive"],
    tag: "Executive",
  },
  {
    title: "Activity Capture Policy",
    description: "Define which activity types count toward rep cadence scores and coaching triggers.",
    route: "/admin/activity-policy",
    roles: ["executive", "manager"],
    tag: "Manager / Executive",
  },
  {
    title: "Profile Settings",
    description: "Your name, email, notification preferences, and password.",
    route: "/settings/account",
    roles: ["executive", "manager", "ae", "bdr", "marketing", "se"],
  },
];

export default function SettingsHub() {
  const [, navigate] = useLocation();
  const session = getSession();
  const role = session?.role ?? "ae";

  const visibleCards = SETTINGS_CARDS.filter((c) => c.roles.includes(role as any));

  return (
    <OperatorShell
      breadcrumbs={[{ label: "Settings" }]}
      pageTitle="Settings"
      pageMeta="Manage your account and workspace preferences"
      showContextTabs={false}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 16,
          marginTop: 8,
        }}
      >
        {visibleCards.map((card) => (
          <button
            key={card.route}
            type="button"
            onClick={() => navigate(card.route)}
            style={{
              textAlign: "left",
              background: "#FFFFFF",
              border: "1px solid #E4E4E7",
              borderRadius: 8,
              padding: "20px 20px 18px",
              cursor: "pointer",
              transition: "box-shadow 120ms",
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.boxShadow = "none";
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: "#0B0B0F", lineHeight: 1.3 }}>
                {card.title}
              </span>
              {card.tag && (
                <span
                  style={{
                    fontSize: 10,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: "#6B6B70",
                    background: "#F4F4F5",
                    borderRadius: 4,
                    padding: "2px 6px",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  {card.tag}
                </span>
              )}
            </div>
            <span style={{ fontSize: 13, color: "#6B6B70", lineHeight: 1.5 }}>
              {card.description}
            </span>
            <span
              style={{
                fontSize: 12,
                color: "#DC2626",
                fontWeight: 500,
                marginTop: 4,
              }}
            >
              Open
            </span>
          </button>
        ))}

        {visibleCards.length === 0 && (
          <div style={{ color: "#6B6B70", fontSize: 13, padding: "24px 0" }}>
            No settings are available for your role.
            Contact <a href="mailto:support_team@mi6group.com" style={{ color: "#DC2626" }}>Attilius Support</a> if you need access.
          </div>
        )}
      </div>

      {/* Support footer */}
      <div
        style={{
          marginTop: 40,
          paddingTop: 20,
          borderTop: "1px solid #E4E4E7",
          fontSize: 12,
          color: "#6B6B70",
        }}
      >
        Questions about a setting?{" "}
        <a href="mailto:support_team@mi6group.com" style={{ color: "#DC2626", textDecoration: "underline" }}>
          Contact Attilius Support · Mi6 Group
        </a>
      </div>
    </OperatorShell>
  );
}
